import { CommonModule } from '@angular/common';
import {
  Component,
  ComponentRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  Type,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ConfirmationService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { StepperModule } from 'primeng/stepper';
import {
  WidgetConfigurationComponent,
  WidgetConfigurationType,
  WidgetType,
} from '../../models/widget-configuration.model';
import { WidgetRegistry } from '../../widget-registry.service';

@Component({
  selector: 'carehub-widget-creation',
  imports: [
    ButtonModule,
    CommonModule,
    DialogModule,
    FormsModule,
    ReactiveFormsModule,
    SelectModule,
    StepperModule,
  ],
  providers: [ConfirmationService],
  templateUrl: './widget-creation.component.html',
  styleUrl: './widget-creation.component.scss',
})
export class WidgetCreationComponent implements OnInit {
  @Input() visible: boolean = false;
  @Input() canAddWidget: boolean = true;
  @Output() widgetTypeSelected = new EventEmitter<WidgetType>();
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() widgetCreated = new EventEmitter<{
    type: WidgetType;
    configuration: WidgetConfigurationType;
  }>();

  @ViewChild('configContainer', { read: ViewContainerRef, static: false })
  configContainer!: ViewContainerRef;

  widgetCreationForm!: FormGroup;
  currentStep: number = 1;
  selectedWidgetType: WidgetType | null = null;
  private configComponentRef: ComponentRef<WidgetConfigurationComponent> | null = null;

  widgetTypes: { label: string; value: WidgetType }[] = [];
  guideLink: string | undefined;

  constructor(
    private formBuilder: FormBuilder,
    private widgetRegistry: WidgetRegistry
  ) {}

  ngOnInit(): void {
    this.widgetCreationForm = this.formBuilder.group({
      widgetType: ['', Validators.required],
    });

    this.widgetTypes = this.widgetRegistry.getAvailableWidgets();

    this.widgetCreationForm.get('widgetType')?.valueChanges.subscribe((selectedValue) => {
      const widgetType: WidgetType = selectedValue?.value || selectedValue;
      this.selectedWidgetType = widgetType;

      if (widgetType) {
        this.widgetTypeSelected.emit(widgetType);
      }

      if (widgetType && this.currentStep === 2) {
        this.loadConfigurationComponent(widgetType);
      }
    });
  }

  get selectedWidgetLabel(): string {
    const selectedValue: { label: string; value: WidgetType } | WidgetType =
      this.widgetCreationForm.get('widgetType')?.value;
    return this.widgetTypes.find(type => type.value === selectedValue)?.label || '';
  }

  loadConfigurationComponent(widgetType: WidgetType): void {
    if (this.configContainer) {
      this.configContainer.clear();
    }
    this.configComponentRef = null;

    const configComponent: Type<WidgetConfigurationComponent> =
      this.widgetRegistry.getConfigurationComponent(widgetType);

    if (configComponent && this.configContainer) {
      this.configComponentRef = this.configContainer.createComponent(configComponent);
    }

    this.guideLink = this.widgetRegistry.getGuideLink(widgetType);
  }

  // Add method to check if configuration is valid
  isConfigurationValid(): boolean {
    return this.configComponentRef?.instance?.isValid?.() || false;
  }

  // Add method to save configuration
  saveConfiguration(): void {
    if (this.configComponentRef?.instance && this.selectedWidgetType) {
      const configuration: WidgetConfigurationType =
        this.configComponentRef.instance.getConfiguration();

      if (this.configComponentRef.instance.isValid()) {
        if (this.canAddWidget) {
          this.widgetCreated.emit({
            type: this.selectedWidgetType,
            configuration: configuration,
          });
          this.close();
        }
      }
    }
  }

  onStepChange(newStep: number | undefined): void {
    this.currentStep = newStep ?? 1;
    if (newStep === 2 && this.selectedWidgetType) {
      setTimeout(() => {
        this.loadConfigurationComponent(this.selectedWidgetType!);
      });
    } else if (newStep === 1) {
      if (this.configContainer) {
        this.configContainer.clear();
      }
      this.configComponentRef = null;
    }
  }

  nextStep(): void {
    if (this.currentStep === 1 && this.widgetCreationForm.valid) {
      this.currentStep = 2;
      const selectedValue: { label: string; value: WidgetType } =
        this.widgetCreationForm.get('widgetType')?.value;
      const widgetType: WidgetType = selectedValue?.value || selectedValue;

      if (widgetType) {
        setTimeout(() => {
          this.loadConfigurationComponent(widgetType);
        });
      }
    }
  }

  prevStep(): void {
    this.currentStep = 1;
    if (this.configContainer) {
      this.configContainer.clear();
    }
    this.configComponentRef = null;
  }

  close(): void {
    this.visible = false;
    this.currentStep = 1;
    this.selectedWidgetType = null;
    if (this.configContainer) {
      this.configContainer.clear();
    }
    this.configComponentRef = null;
    this.widgetCreationForm.reset();
    this.visibleChange.emit(this.visible);
  }
}
